import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Injectable } from '@angular/core';
import { defer, finalize, from } from 'rxjs';
import { MembershipService } from '../../my-membership/services/membershipguard.service';
import { environment } from '../../../../environments/environment';
import { AuthService } from './auth.service';
import { AppLoadingService } from './app-loading.service';

@Injectable({ providedIn: 'root' })
export class MembershipGuard implements CanActivate {
  constructor(
    private msalService: MsalService,
    private router: Router,
    private membershipService: MembershipService,
    private authService: AuthService,
    private loading: AppLoadingService
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    this.loading.show();

    return defer(() => from(this.runMembershipCheck())).pipe(finalize(() => this.loading.hide()));
  }

  private async runMembershipCheck(): Promise<boolean | UrlTree> {
    let account = this.msalService.instance.getActiveAccount();

    if (!account) {
      const desiredPolicy = sessionStorage.getItem('activePolicy')?.toLowerCase();
      const accounts = this.msalService.instance.getAllAccounts();

      const matchingAccount = desiredPolicy
        ? accounts.find(
            (acc) =>
              ((acc.idTokenClaims as any)?.tfp || (acc.idTokenClaims as any)?.acr || '').toLowerCase() === desiredPolicy
          )
        : null;

      const accountToUse = matchingAccount ?? (accounts.length === 1 ? accounts[0] : null);

      if (accountToUse) {
        this.msalService.instance.setActiveAccount(accountToUse);
        account = accountToUse;
      }
    }

    if (!account) {
      console.warn('❌ Ingen aktiv konto -> redirecter til /auth');
      return this.router.parseUrl('/auth');
    }

    const policy =
      (account.idTokenClaims as any)?.tfp ||
      (account.idTokenClaims as any)?.acr ||
      sessionStorage.getItem('activePolicy') ||
      environment.azureB2C.policies.bankId.name;

    const authority = this.authService.buildAuthority(policy);

    try {
      await this.msalService.instance.acquireTokenSilent({
        account,
        authority,
        scopes: environment.azureB2C.msalScopes,
      });

      const isMember = await this.membershipService.ensureIsMember();

      if (!isMember) {
        return this.router.parseUrl('/no-access');
      }
      this.membershipService.prefetchMember();
      return true;

    } catch (err: any) {
      console.warn('🚫 Feil i guard:', err);

      const needsInteraction =
        err?.errorCode === 'interaction_required' ||
        err?.errorCode === 'monitor_window_timeout' ||
        err?.errorCode === 'login_required' ||
        err?.errorCode === 'consent_required';

      if (needsInteraction) {
        localStorage.setItem('authError', 'session_expired');
        await this.authService.clearMsalState();
        sessionStorage.removeItem('activePolicy');
        return this.router.parseUrl('/auth');
      }

      return this.router.createUrlTree(['/no-access'], {
        queryParams: {
          status: err.status ?? 'unknown',
        },
      });
    }
  }
}

